type TrackerType = "countly" | "google";

interface TrackEventPayload {
  key: string;
  count?: number;
  segmentation?: Record<string, unknown>;
}

function getCountly() {
  const { $countly } = useNuxtApp();
  return $countly;
}

function getGtag(): ((...args: unknown[]) => void) | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as any).gtag;
}

const trackers: Record<
  TrackerType,
  {
    trackPageview: (pageName: string) => void;
    trackEvent: (payload: TrackEventPayload) => void;
  }
> = {
  countly: {
    trackPageview(pageName: string) {
      getCountly()?.track_pageview(pageName);
    },
    trackEvent(payload: TrackEventPayload) {
      getCountly()?.add_event({
        key: payload.key,
        count: payload.count ?? 1,
        segmentation: payload.segmentation,
      });
    },
  },
  google: {
    trackPageview(pageName: string) {
      getGtag()?.("event", "page_view", { page_title: pageName });
    },
    trackEvent(payload: TrackEventPayload) {
      getGtag()?.("event", payload.key, {
        ...payload.segmentation,
        value: payload.count ?? 1,
      });
    },
  },
};

export function useEventTracker(type: TrackerType = "countly") {
  const tracker = trackers[type];
  if (!tracker) {
    if (process.dev)
      console.warn(`[EventTracker] Unknown tracker type: "${type}"`);
    return {
      trackPageview: () => {},
      trackEvent: () => {},
      trackLocation: async () => {},
    };
  }

  async function trackLocation() {
    const { getLocation } = useGeolocation();
    const location = await getLocation();
    if (location) {
      const countly = getCountly();
      if (countly) {
        countly.track_location({
          lat: String(location.latitude),
          lng: String(location.longitude),
        });
      }
    }
  }

  return { ...tracker, trackLocation };
}
